import { signedApiClient } from './apiClient';
import { serializeGrant } from '@/models/grant';
import type {
  InviteBatchRequest,
  InviteBatchResponse,
  InviteEditRequest,
  InviteResendResponse
} from '@/models/invites';

/**
 * spdata-api's one invite service on `study/:id/invite` (users decision 85), each route behind Manage
 * users, with the study taken from the URL.
 */
class InviteService {

  private studyEndpoint: string = '';

  initService(studyEndpoint: string) {
    this.studyEndpoint = studyEndpoint;
  }

  /** One batch in one transaction, capped at 50, answering one result per address (decisions 80 to 82). */
  async sendInvites(studyId: string, batch: InviteBatchRequest): Promise<InviteBatchResponse> {
    const uri = `${this.studyEndpoint}/${studyId}/invite`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, {
      addresses: [...batch.addresses],
      grant: serializeGrant(batch.grant),
      groups: [...batch.groups],
      ...(batch.notificationProfile && { notificationProfile: batch.notificationProfile })
    });
    return response.data;
  }

  /** A pending or expired invite's grant and groups (decision 88). */
  async editInvite(studyId: string, inviteId: string, edit: InviteEditRequest) {
    const uri = `${this.studyEndpoint}/${studyId}/invite/${inviteId}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, {
      ...(edit.grant && { grant: serializeGrant(edit.grant) }),
      ...(edit.groups && { groups: { add: [...edit.groups.add], remove: [...edit.groups.remove] } })
    });
  }

  /** Seven more days and the email again; 429 within 10 minutes of the last (decision 79). */
  async resendInvite(studyId: string, inviteId: string): Promise<InviteResendResponse> {
    const uri = `${this.studyEndpoint}/${studyId}/invite/${inviteId}/resend`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri);
    return response.data;
  }

  /** Cancels a pending or expired invite, or removes a declined one (decisions 84 and 94). */
  async cancelInvite(studyId: string, inviteId: string) {
    const uri = `${this.studyEndpoint}/${studyId}/invite/${inviteId}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }
}

export default new InviteService();
